import React, { useEffect, useMemo, useState } from "react";
import { adminListLeads, adminUpdateLead, adminDeleteLead } from "@/lib/api";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Mail, Phone, Trash2, Search, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "callback", "successful", "lost"];
const STATUS_COLOR = {
    new: "bg-brand-accent/20 text-brand-accent border-brand-accent/40",
    contacted: "bg-[#3E7A5D]/20 text-[#3E7A5D] border-[#3E7A5D]/40",
    callback: "bg-[#D9A84B]/20 text-[#D9A84B] border-[#D9A84B]/40",
    successful: "bg-brand-primary/15 text-brand-primary border-brand-primary/40",
    lost: "bg-[#9A4040]/15 text-[#9A4040] border-[#9A4040]/40",
};

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: "all", source: "all", q: "" });
    const [expanded, setExpanded] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.status !== "all") params.status = filter.status;
            if (filter.source !== "all") params.source = filter.source;
            const data = await adminListLeads(params);
            setLeads(data);
        } catch {
            toast.error("Could not load leads");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.status, filter.source]);

    const sources = useMemo(() => {
        const set = new Set(leads.map((l) => l.source).filter(Boolean));
        return ["all", ...set];
    }, [leads]);

    const filtered = useMemo(() => {
        const q = filter.q.trim().toLowerCase();
        if (!q) return leads;
        return leads.filter(
            (l) =>
                [l.name, l.email, l.phone, l.service, l.message]
                    .filter(Boolean)
                    .some((f) => String(f).toLowerCase().includes(q))
        );
    }, [filter.q, leads]);

    const setStatus = async (lead, status) => {
        try {
            const updated = await adminUpdateLead(lead.id, { status });
            setLeads((arr) => arr.map((l) => (l.id === lead.id ? updated : l)));
            toast.success(`Marked as ${status}`);
        } catch {
            toast.error("Failed to update");
        }
    };

    const remove = async (lead) => {
        if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
        try {
            await adminDeleteLead(lead.id);
            setLeads((arr) => arr.filter((l) => l.id !== lead.id));
            toast.success("Deleted");
        } catch {
            toast.error("Failed to delete");
        }
    };

    return (
        <div data-testid="admin-leads" className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
                        Inbox
                    </div>
                    <h1 className="font-serif-editorial text-4xl text-brand-primary mt-1">
                        Leads
                    </h1>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                        <input
                            placeholder="Search name, email…"
                            value={filter.q}
                            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                            data-testid="leads-search"
                            className="pl-9 pr-3 py-2.5 bg-brand-surface border border-brand-border text-sm w-56 focus:outline-none focus:border-brand-primary"
                        />
                    </div>
                    <FilterSelect
                        label="Status"
                        value={filter.status}
                        onChange={(v) => setFilter({ ...filter, status: v })}
                        options={["all", ...STATUSES]}
                        testid="leads-filter-status"
                    />
                    <FilterSelect
                        label="Source"
                        value={filter.source}
                        onChange={(v) => setFilter({ ...filter, source: v })}
                        options={sources}
                        testid="leads-filter-source"
                    />
                </div>
            </div>

            <div className="bg-brand-surface border border-brand-border">
                <div className="grid grid-cols-[40px_1.2fr_1.6fr_1fr_0.8fr_0.8fr] px-5 py-3 border-b border-brand-border text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                    <div />
                    <div>Name</div>
                    <div className="hidden md:block">Contact</div>
                    <div className="hidden md:block">Source / Service</div>
                    <div>Status</div>
                    <div className="text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-10 text-brand-muted text-sm">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-brand-muted text-sm">No leads match your filter.</div>
                ) : (
                    filtered.map((l) => (
                        <div
                            key={l.id}
                            data-testid={`lead-row-${l.id}`}
                            className="border-b border-brand-border last:border-b-0"
                        >
                            <div className="grid grid-cols-[40px_1.2fr_1.6fr_1fr_0.8fr_0.8fr] items-center px-5 py-4 hover:bg-brand-surfaceAlt/40 transition-colors">
                                <button
                                    onClick={() => setExpanded((x) => (x === l.id ? null : l.id))}
                                    className="text-brand-muted hover:text-brand-primary"
                                    data-testid={`lead-expand-${l.id}`}
                                    aria-label="Toggle details"
                                >
                                    {expanded === l.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>
                                <div>
                                    <div className="font-bold text-brand-primary">{l.name}</div>
                                    <div className="text-xs text-brand-muted">
                                        {new Date(l.created_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="hidden md:block text-sm text-brand-muted">
                                    <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-brand-primary">
                                        <Mail size={12} /> {l.email}
                                    </a>
                                    {l.phone && (
                                        <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 mt-1 hover:text-brand-primary">
                                            <Phone size={12} /> {l.phone}
                                        </a>
                                    )}
                                </div>
                                <div className="hidden md:block text-sm text-brand-muted">
                                    <div className="text-brand-primary font-semibold capitalize">
                                        {((l.source || "")
                                            .replace(/_/g, " ")
                                            .replace("ai assistant", "AI Helper"))}
                                    </div>
                                    {l.service && <div className="text-xs">{l.service}</div>}
                                </div>
                                <div>
                                    <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                                        <SelectTrigger
                                            data-testid={`lead-status-${l.id}`}
                                            className={`h-9 text-xs font-bold uppercase tracking-wide border ${STATUS_COLOR[l.status] || STATUS_COLOR.new}`}
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUSES.map((s) => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="text-right">
                                    <button
                                        onClick={() => remove(l)}
                                        data-testid={`lead-delete-${l.id}`}
                                        className="text-brand-muted hover:text-[#9A4040] p-2"
                                        aria-label="Delete"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            {expanded === l.id && (
                                <div className="px-6 pb-6 pl-14 text-sm text-brand-muted space-y-3 bg-brand-bg/40">
                                    <DetailRow label="Message">
                                        <pre className="whitespace-pre-wrap font-sans text-sm">
                                            {l.message || "—"}
                                        </pre>
                                    </DetailRow>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <DetailRow label="Postcode">{l.postcode || "—"}</DetailRow>
                                        <DetailRow label="Goal">{l.goal || "—"}</DetailRow>
                                        <DetailRow label="Timeline">{l.timeline || "—"}</DetailRow>
                                        <DetailRow label="Property value">{l.property_value != null ? `£${Number(l.property_value).toLocaleString()}` : "—"}</DetailRow>
                                        <DetailRow label="Deposit">{l.deposit != null ? `£${Number(l.deposit).toLocaleString()}` : "—"}</DetailRow>
                                        <DetailRow label="Income">{l.income != null ? `£${Number(l.income).toLocaleString()}` : "—"}</DetailRow>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                {label}
            </span>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger
                    data-testid={testid}
                    className="h-9 bg-brand-surface border border-brand-border text-sm min-w-[120px]"
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {options.map((o) => (
                        <SelectItem key={o} value={o}>
                            {o === "all" ? "All" : o.replace(/_/g, " ")}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function DetailRow({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                {label}
            </div>
            <div className="text-brand-primary mt-1">{children}</div>
        </div>
    );
}
